import React, { useEffect, useMemo, useState } from "react";
import { api, romeDateString } from "@/lib/api";
import { Receipt, FileText, Search, Calendar, Printer, X, Eye } from "lucide-react";

const ScontriniFatture = () => {
    const [docs, setDocs] = useState([]);
    const [filter, setFilter] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [amountMin, setAmountMin] = useState("");
    const [amountMax, setAmountMax] = useState("");
    const [textSearch, setTextSearch] = useState("");
    const [preview, setPreview] = useState(null);

    const load = async () => {
        const url = filter ? `/documents?type=${filter}` : "/documents";
        const { data } = await api.get(url);
        setDocs(data);
    };
    useEffect(() => { load(); }, [filter]);

    // Filtri client-side (data, importo, ricerca testuale su numero/cliente)
    const filtered = useMemo(() => {
        return docs.filter((d) => {
            // Data — usa giorno italiano del campo created_at
            if (dateFrom || dateTo) {
                const docDate = d.created_at ? d.created_at.slice(0, 10) : "";
                if (dateFrom && docDate < dateFrom) return false;
                if (dateTo && docDate > dateTo) return false;
            }
            // Importo
            const amt = parseFloat(d.amount) || 0;
            if (amountMin !== "" && amt < parseFloat(amountMin)) return false;
            if (amountMax !== "" && amt > parseFloat(amountMax)) return false;
            // Testo: numero o nome cliente
            if (textSearch.trim()) {
                const q = textSearch.toLowerCase();
                const num = (d.number || "").toLowerCase();
                const cust = (d.invoice_data?.name || "").toLowerCase();
                if (!num.includes(q) && !cust.includes(q)) return false;
            }
            return true;
        });
    }, [docs, dateFrom, dateTo, amountMin, amountMax, textSearch]);

    const clearFilters = () => {
        setDateFrom(""); setDateTo(""); setAmountMin(""); setAmountMax(""); setTextSearch("");
    };

    const setQuickDate = (preset) => {
        const today = romeDateString();
        if (preset === "today") {
            setDateFrom(today); setDateTo(today);
        } else if (preset === "week") {
            const d = new Date();
            d.setDate(d.getDate() - 7);
            setDateFrom(romeDateString(d)); setDateTo(today);
        } else if (preset === "month") {
            const d = new Date();
            d.setMonth(d.getMonth() - 1);
            setDateFrom(romeDateString(d)); setDateTo(today);
        }
    };

    const totalFiltered = filtered.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);

    return (
        <div className="space-y-6" data-testid="scontrini-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Documenti Fiscali</div>
                    <h1 className="font-display text-4xl font-bold">Scontrini & Fatture</h1>
                    <p className="text-elegant text-sm mt-2">
                        Cerca, filtra e ristampa copie dei documenti emessi.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setFilter("")} className={`h-11 px-4 rounded-md border text-sm ${!filter ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44]"}`}>Tutti</button>
                    <button onClick={() => setFilter("scontrino")} className={`h-11 px-4 rounded-md border text-sm ${filter === "scontrino" ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44]"}`}>Scontrini</button>
                    <button onClick={() => setFilter("fattura")} className={`h-11 px-4 rounded-md border text-sm ${filter === "fattura" ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44]"}`}>Fatture</button>
                </div>
            </div>

            {/* Pannello filtri */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 space-y-3" data-testid="filters-panel">
                <div className="flex items-center gap-2 text-elegant text-xs uppercase tracking-wider">
                    <Search className="w-3.5 h-3.5" /> Ricerca
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="lg:col-span-2">
                        <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Numero o cliente</label>
                        <input type="text" value={textSearch} onChange={(e) => setTextSearch(e.target.value)}
                            placeholder="Es. 5F3A2B o Mario Rossi"
                            data-testid="search-text"
                            className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Importo min €</label>
                            <input type="number" step="0.01" value={amountMin} onChange={(e) => setAmountMin(e.target.value)}
                                data-testid="search-amount-min"
                                className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none text-sm" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">max €</label>
                            <input type="number" step="0.01" value={amountMax} onChange={(e) => setAmountMax(e.target.value)}
                                data-testid="search-amount-max"
                                className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none text-sm" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Dal</label>
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                data-testid="search-date-from"
                                className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none text-sm" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Al</label>
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                data-testid="search-date-to"
                                className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none text-sm" />
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex gap-1 flex-wrap">
                        <button onClick={() => setQuickDate("today")} className="px-3 h-8 text-xs border border-[#1f2c44] hover:border-brand rounded text-elegant hover:text-brand">Oggi</button>
                        <button onClick={() => setQuickDate("week")} className="px-3 h-8 text-xs border border-[#1f2c44] hover:border-brand rounded text-elegant hover:text-brand">Ultimi 7 gg</button>
                        <button onClick={() => setQuickDate("month")} className="px-3 h-8 text-xs border border-[#1f2c44] hover:border-brand rounded text-elegant hover:text-brand">Ultimo mese</button>
                        <button onClick={clearFilters} className="px-3 h-8 text-xs border border-[#1f2c44] hover:border-amber-500 rounded text-elegant hover:text-amber-400 flex items-center gap-1">
                            <X className="w-3 h-3" /> Reset
                        </button>
                    </div>
                    <div className="text-sm" data-testid="results-summary">
                        <span className="text-elegant">{filtered.length} risultati ·</span>
                        <span className="text-brand font-bold ml-1">€ {totalFiltered.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            <div className="bg-surface border border-[#1f2c44] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-surface-elevated">
                        <tr className="text-left text-elegant text-xs uppercase tracking-wider">
                            <th className="px-4 py-3">Tipo</th>
                            <th className="px-4 py-3">Numero</th>
                            <th className="px-4 py-3">Data/Ora</th>
                            <th className="px-4 py-3">Importo</th>
                            <th className="px-4 py-3">Metodo</th>
                            <th className="px-4 py-3">Cliente</th>
                            <th className="px-4 py-3 text-right">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && <tr><td colSpan="7" className="px-4 py-12 text-center text-elegant">Nessun documento corrisponde ai filtri.</td></tr>}
                        {filtered.map((d) => (
                            <tr key={d.id} className="border-t border-[#1f2c44] hover:bg-[#0e1626]" data-testid={`doc-${d.number}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2 text-brand uppercase tracking-wider text-xs">
                                        {d.type === "fattura" ? <FileText className="w-4 h-4" /> : <Receipt className="w-4 h-4" />} {d.type}
                                    </div>
                                </td>
                                <td className="px-4 py-3 font-mono">{d.number}</td>
                                <td className="px-4 py-3 text-elegant whitespace-nowrap">{new Date(d.created_at).toLocaleString("it-IT")}</td>
                                <td className="px-4 py-3 font-display font-bold text-brand">€ {d.amount.toFixed(2)}</td>
                                <td className="px-4 py-3 uppercase tracking-wider text-xs">{d.method}</td>
                                <td className="px-4 py-3 text-elegant truncate max-w-[180px]">{d.invoice_data?.name || "—"}</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex justify-end gap-1">
                                        <button onClick={() => setPreview(d)} title="Visualizza"
                                            data-testid={`doc-view-${d.number}`}
                                            className="w-8 h-8 rounded border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand flex items-center justify-center">
                                            <Eye className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => reprintDocument(d)} title="Ristampa copia"
                                            data-testid={`doc-reprint-${d.number}`}
                                            className="w-8 h-8 rounded border border-brand bg-brand/10 hover:bg-brand/20 text-brand flex items-center justify-center">
                                            <Printer className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {preview && (
                <DocPreviewModal doc={preview} onClose={() => setPreview(null)} onReprint={() => reprintDocument(preview)} />
            )}
        </div>
    );
};

// Render scontrino in 80mm-style HTML e apri popup stampa
const reprintDocument = (d) => {
    const w = window.open("", "_blank", "width=380,height=720");
    if (!w) { alert("Popup bloccato dal browser. Abilita i popup per stampare."); return; }
    w.document.write(buildDocHtml(d, { copy: true }));
    w.document.close();
};

const buildDocHtml = (d, opts = {}) => {
    const copy = opts.copy ?? false;
    const dt = new Date(d.created_at).toLocaleString("it-IT");
    const itemsRows = (d.items || []).map((it) => {
        const extrasSum = (it.extras || []).reduce((s, e) => s + (parseFloat(e.price) || 0), 0);
        const gross = (it.price + extrasSum) * it.quantity;
        const net = gross - (it.discount_amt || 0) * 1
            - gross * ((it.discount_pct || 0) / 100);
        const mainRow = `<tr><td>${escapeHtml(it.name)}</td><td class="r">${it.quantity}</td><td class="r">€ ${net.toFixed(2)}</td></tr>`;
        const extraRows = (it.extras || []).map((ex) => {
            const pStr = ex.price > 0 ? `+€${parseFloat(ex.price).toFixed(2)}` : "";
            return `<tr><td class="small" style="padding-left:8px">+ ${escapeHtml(ex.label)} ${pStr}</td><td></td><td></td></tr>`;
        }).join("");
        return mainRow + extraRows;
    }).join("");
    const disc = d.discount_amt || d.discount_pct
        ? `<tr><td colspan="2">Sconto${d.discount_pct ? ` ${d.discount_pct}%` : ""}</td><td class="r">- € ${(d.discount_amt || 0).toFixed(2)}</td></tr>`
        : "";
    const inv = d.invoice_data || {};
    const invBlock = d.type === "fattura" && (inv.name || inv.piva || inv.cf) ? `
        <hr>
        <div class="small"><strong>FATTURATO A:</strong></div>
        <div class="small">${escapeHtml(inv.name || "")}${inv.address ? "<br>" + escapeHtml(inv.address) : ""}</div>
        ${inv.cf ? `<div class="small">CF: ${escapeHtml(inv.cf)}</div>` : ""}
        ${inv.piva ? `<div class="small">P.IVA: ${escapeHtml(inv.piva)}</div>` : ""}
        ${inv.sdi ? `<div class="small">SDI: ${escapeHtml(inv.sdi)}</div>` : ""}
        ${inv.pec ? `<div class="small">PEC: ${escapeHtml(inv.pec)}</div>` : ""}
    ` : "";
    return `
<html>
<head>
<title>${d.type === "fattura" ? "Fattura" : "Scontrino"} ${d.number}</title>
<style>
  @page { size: 80mm auto; margin: 5mm; }
  body { font-family: "Courier New", monospace; font-size: 12px; width: 70mm; margin: 0 auto; color:#000; }
  h1 { font-size: 14px; text-align: center; margin: 0 0 6px; letter-spacing: 1px; }
  .center { text-align: center; }
  .small { font-size: 10px; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 2px 0; vertical-align: top; }
  .r { text-align: right; }
  hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
  .copy-banner { text-align: center; font-size: 10px; padding: 4px; border: 1px dashed #c00; color: #c00; margin-bottom: 6px; letter-spacing: 1px; font-weight: bold; }
  .total { font-size: 16px; font-weight: bold; }
  .footer { font-size: 9px; text-align: center; margin-top: 8px; }
</style>
</head>
<body>
${copy ? '<div class="copy-banner">★ COPIA RISTAMPA ★</div>' : ""}
<h1>${d.type === "fattura" ? "FATTURA" : "SCONTRINO"}</h1>
<div class="center small">Smart Table 360 by renova360</div>
<hr>
<div class="small">Documento N°: <strong>${d.number}</strong></div>
<div class="small">Data: ${dt}</div>
${d.table_id ? `<div class="small">Tavolo: ${d.table_id}</div>` : ""}
<hr>
<table>
  <thead><tr><td><strong>Articolo</strong></td><td class="r"><strong>Qt</strong></td><td class="r"><strong>€</strong></td></tr></thead>
  <tbody>${itemsRows}</tbody>
</table>
${disc ? `<hr><table>${disc}</table>` : ""}
<hr>
<table>
  <tr class="total"><td colspan="2">TOTALE</td><td class="r">€ ${d.amount.toFixed(2)}</td></tr>
  <tr><td colspan="2">Pagamento</td><td class="r">${escapeHtml(d.method || "")}</td></tr>
</table>
${invBlock}
<hr>
<div class="footer">
  Grazie e arrivederci<br>
  ${copy ? "Copia non valida ai fini fiscali" : "Documento commerciale"}
</div>
<script>
  setTimeout(function(){ window.print(); }, 250);
</script>
</body>
</html>`;
};

const escapeHtml = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const DocPreviewModal = ({ doc, onClose, onReprint }) => {
    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="doc-preview-modal">
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
                <div className="sticky top-0 bg-white border-b flex items-center justify-between px-4 py-2 z-10">
                    <div className="text-sm font-bold text-gray-800">{doc.type === "fattura" ? "Fattura" : "Scontrino"} {doc.number}</div>
                    <div className="flex gap-2">
                        <button onClick={onReprint} data-testid="preview-reprint-button"
                            className="px-3 h-9 bg-brand text-deep font-bold rounded text-xs flex items-center gap-1">
                            <Printer className="w-3.5 h-3.5" /> Ristampa
                        </button>
                        <button onClick={onClose} className="px-3 h-9 border border-gray-300 rounded text-xs text-gray-700">Chiudi</button>
                    </div>
                </div>
                <iframe
                    srcDoc={buildDocHtml(doc, { copy: false })}
                    title={`Doc ${doc.number}`}
                    className="w-full"
                    style={{ height: "600px", border: 0 }}
                />
            </div>
        </div>
    );
};

export default ScontriniFatture;
